import { createAxiosThunk } from 'utils/reduxUtil'
import dashboard from 'api/axios/foka/dashboard'
import { TREE_SORT, TREE_STATUS } from 'features/dashboard/tree/StatusAndSort'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { serializeError } from 'utils'
import { CampaignTypes } from '../../Codes'

export const fetchTreeCampaigns = createAsyncThunk(
  'dashboard/fetchTreeCampaigns',
  async (noArgs, { getState, dispatch, rejectWithValue }) => {
    const { status, sort, actives } = getState().dashboard.tree
    try {
      const { data } = await dashboard.getTreeCampaigns(status, sort)
      const campaignIds = data.campaigns
        .filter(({ id }) => actives.includes(id))
        .map(({ id }) => id)
      if (campaignIds.length > 0) {
        campaignIds.forEach(campaignId => {
          dispatch(fetchTreeAdGroups(campaignId))
        })
      }
      return data
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

export const fetchTreeAdGroups = createAxiosThunk(
  'dashboard/fetchTreeAdGroups',
  (campaignId, { getState }) => {
    const { status } = getState().dashboard.tree
    return campaignId ? dashboard.getTreeAdGroups(campaignId, status) : null
  }
)

const initialState = {
  visible:
    JSON.parse(localStorage.getItem('treeVisible')) === null
      ? true
      : JSON.parse(localStorage.getItem('treeVisible')),
  status: TREE_STATUS.ALL,
  sort: TREE_SORT.NAME_ASC,
  isLoading: false,
  campaigns: [],
  adGroups: {},
  adGroupLoadings: {},
  actives: [],
  expandCampaignTypes: [CampaignTypes.PREMIUM_LINK, CampaignTypes.TALK_CHANNEL],
  selectedCampaignId: null,
  selectedAdgroupId: null,
}

const dashboardTreeSlice = createSlice({
  name: 'dashboard/tree',
  initialState,

  reducers: {
    addActives: (state, { payload: campaignId }) => {
      if (!state.actives.includes(campaignId)) {
        state.actives.push(campaignId)
      }
    },
    removeActives: (state, { payload: campaignId }) => {
      const index = state.actives.indexOf(campaignId)
      if (index !== -1) state.actives.splice(index, 1)
    },
    resetActives: state => {
      state.actives = []
    },
    toggleCampaignType: (state, { payload: campaignType }) => {
      const index = state.expandCampaignTypes.indexOf(campaignType)
      if (index === -1) {
        state.expandCampaignTypes.push(campaignType)
      } else {
        state.expandCampaignTypes.splice(index, 1)
      }
    },
    setStatus: (state, { payload }) => {
      state.status = payload
    },
    setSort: (state, { payload }) => {
      state.sort = payload
    },
    setVisible: (state, { payload: visible }) => {
      localStorage.setItem('treeVisible', visible)
      state.visible = visible
      if (!visible) {
        state.campaigns = []
        state.adGroups = {}
        state.adGroupLoadings = {}
      }
    },
    setCampaignId: (state, { payload: campaignId }) => {
      state.selectedCampaignId = campaignId
      state.selectedAdgroupId = null
    },
    setAdgroupId: (state, { payload: adGroupId }) => {
      state.selectedAdgroupId = adGroupId
      state.selectedCampaignId = null
    },
    addExpandCampaignType: (state, { payload: campaignType }) => {
      if (!state.expandCampaignTypes.includes(campaignType)) {
        state.expandCampaignTypes.push(campaignType)
      }
    },
    reset: () => initialState,
  },

  extraReducers: builder => {
    builder.addCase(fetchTreeCampaigns.pending, state => {
      state.adGroupLoadings = {}
      state.isLoading = true
    })

    builder.addCase(fetchTreeCampaigns.fulfilled, (state, { payload }) => {
      state.isLoading = false
      const { campaigns, adGroups } = payload.campaigns.reduce(
        (acc, cur) => {
          const { adGroups: groups = [], ...rest } = cur
          acc.campaigns.push({
            ...rest,
            adGroupIds: groups.map(({ id }) => id),
          })
          groups.forEach(adGroup => {
            acc.adGroups[adGroup.id] = {
              ...adGroup,
              loaded: false,
            }
          })
          return acc
        },
        {
          campaigns: [],
          adGroups: {},
        }
      )
      state.campaigns = campaigns
      state.adGroups = adGroups
    })

    builder.addCase(fetchTreeCampaigns.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(
      fetchTreeAdGroups.pending,
      (state, { meta: { arg: campaignId } }) => {
        state.adGroupLoadings[campaignId] = true
        if (!state.actives.includes(campaignId)) {
          state.actives.push(campaignId)
        }
      }
    )

    builder.addCase(
      fetchTreeAdGroups.fulfilled,
      (state, { payload: { adGroups }, meta: { arg: campaignId } }) => {
        const update = adGroups.reduce((acc, cur) => {
          acc[cur.id] = {
            ...cur,
            loaded: true,
          }
          return acc
        }, {})
        state.adGroups = {
          ...state.adGroups,
          ...update,
        }
        state.adGroupLoadings[campaignId] = false
      }
    )

    builder.addCase(
      fetchTreeAdGroups.rejected,
      (state, { meta: { arg: campaignId } }) => {
        state.adGroupLoadings[campaignId] = false
      }
    )
  },
})

const { actions, reducer } = dashboardTreeSlice
export const {
  addActives,
  removeActives,
  resetActives,
  toggleCampaignType,
  setStatus,
  setSort,
  setVisible,
  setCampaignId,
  setAdgroupId,
  addExpandCampaignType,
  reset,
} = actions
export const dashboardTreeActions = actions
export default reducer
